import React, { useState, useEffect } from 'react';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { BarChart3, RefreshCw, MessageSquare, Car, Package, Bus, Truck, TrendingUp, Users as UsersIcon, Zap, Send } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import api from '../../utils/api';
import AdminLayout from '../../components/admin/AdminLayout';

const VERTICALS = {
  rides:    { label: 'Rides',    Icon: Car,     tint: 'bg-blue-50 text-blue-700 border-blue-200' },
  packages: { label: 'Packages', Icon: Package, tint: 'bg-amber-50 text-amber-700 border-amber-200' },
  bus:      { label: 'Bus',      Icon: Bus,     tint: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
  cargo:    { label: 'Cargo',    Icon: Truck,   tint: 'bg-orange-50 text-orange-700 border-orange-200' },
};

const PERIODS = [
  { v: 7, label: '7d' },
  { v: 30, label: '30d' },
  { v: 90, label: '90d' },
];

const fmtK = (n) => `K${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const AdminWhatsAppAnalytics = () => {
  const [period, setPeriod] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  // Discovery boost composer
  const [boostOpen, setBoostOpen] = useState(false);
  const [boost, setBoost] = useState({
    source_vertical: 'packages',
    target_vertical: 'cargo',
    message: '🚛 New on Tekstar: book a truck/van up to 20 tons right here on WhatsApp. Reply *4* or *CARGO* to try it.',
    channel: 'whatsapp',
  });
  const [boostEligible, setBoostEligible] = useState(null);
  const [boostBusy, setBoostBusy] = useState(false);
  const [boostHistory, setBoostHistory] = useState([]);

  const fetchAnalytics = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/whatsapp/analytics?days=${period}`);
      setData(data);
    } catch {
      toast({ title: 'Failed to load analytics', variant: 'destructive' });
    } finally { setLoading(false); }
  };
  const fetchHistory = async () => {
    try {
      const { data } = await api.get('/whatsapp/discovery-boost/history?limit=10');
      setBoostHistory(data.items || []);
    } catch { setBoostHistory([]); }
  };
  useEffect(() => { fetchAnalytics(); /* eslint-disable-next-line */ }, [period]);
  useEffect(() => { fetchHistory(); }, []);

  const previewBoost = async () => {
    setBoostBusy(true);
    try {
      const { data } = await api.post('/whatsapp/discovery-boost', { ...boost, dry_run: true });
      setBoostEligible(data.eligible);
      toast({ title: `${data.eligible} user(s) match` });
    } catch (err) {
      toast({ title: 'Preview failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBoostBusy(false); }
  };
  const sendBoost = async () => {
    if (!window.confirm(`Send this ${boost.channel.toUpperCase()} boost to ${boostEligible ?? '?'} user(s)?`)) return;
    setBoostBusy(true);
    try {
      const { data } = await api.post('/whatsapp/discovery-boost', { ...boost, dry_run: false });
      toast({ title: 'Boost sent', description: `Delivered ${data.sent}/${data.eligible}` });
      setBoostEligible(null);
      fetchHistory();
    } catch (err) {
      toast({ title: 'Send failed', description: err.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setBoostBusy(false); }
  };

  const totals = data?.totals || {};
  const verticals = data?.verticals || [];
  const dropOff = data?.drop_off_stages || [];

  return (
    <AdminLayout>
      <div className="max-w-6xl mx-auto p-4 md:p-6 space-y-6" data-testid="admin-whatsapp-analytics-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <BarChart3 className="h-6 w-6 text-pink-500" /> WhatsApp Analytics
            </h1>
            <p className="text-sm text-gray-500">
              Funnel conversion, GMV and channel mix for the Tekstar WhatsApp bot.
            </p>
          </div>
          <div className="flex gap-2">
            <div className="flex bg-white border rounded-md overflow-hidden" data-testid="analytics-period-picker">
              {PERIODS.map(p => (
                <button
                  key={p.v}
                  onClick={() => setPeriod(p.v)}
                  className={`px-3 py-1.5 text-xs font-medium ${period === p.v ? 'bg-pink-500 text-white' : 'text-gray-600'}`}
                  data-testid={`analytics-period-${p.v}`}
                >{p.label}</button>
              ))}
            </div>
            <Button variant="outline" onClick={fetchAnalytics} data-testid="analytics-refresh-btn">
              <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} /> Refresh
            </Button>
          </div>
        </div>

        {/* Totals strip */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-totals-strip">
          {[
            { label: 'Active users', value: totals.active_users, Icon: UsersIcon, format: String, tint: 'text-pink-600' },
            { label: 'Started', value: totals.started, Icon: MessageSquare, format: String, tint: 'text-indigo-600' },
            { label: 'Completed', value: totals.completed, Icon: TrendingUp, format: String, tint: 'text-emerald-600' },
            { label: 'Conversion', value: totals.conversion_pct, Icon: TrendingUp, format: (v) => `${v || 0}%`, tint: 'text-amber-600' },
            { label: 'GMV', value: totals.gmv, Icon: TrendingUp, format: fmtK, tint: 'text-sky-600' },
          ].map(({ label, value, Icon, format, tint }) => (
            <Card key={label} className="p-4">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
                <Icon className={`h-4 w-4 ${tint}`} />
              </div>
              <p className="text-2xl font-bold mt-1" data-testid={`analytics-total-${label.toLowerCase().replace(/\s+/g, '-')}`}>
                {format(value)}
              </p>
            </Card>
          ))}
        </div>

        {/* Funnel per vertical */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Funnel by vertical</h3>
          <div className="grid md:grid-cols-2 gap-4" data-testid="analytics-vertical-grid">
            {verticals.map(v => {
              const meta = VERTICALS[v.vertical] || { label: v.vertical, Icon: MessageSquare, tint: 'bg-gray-50 text-gray-700 border-gray-200' };
              const { Icon, label, tint } = meta;
              const convPct = v.conversion_pct;
              const mixTotal = Object.values(v.channel_mix).reduce((a, b) => a + b, 0) || 1;
              return (
                <Card key={v.vertical} className="p-5 space-y-3" data-testid={`analytics-vertical-${v.vertical}`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`p-1.5 rounded ${tint}`}><Icon className="h-4 w-4" /></span>
                      <h4 className="font-bold">{label}</h4>
                    </div>
                    <p className="text-xl font-bold">{fmtK(v.gmv)}</p>
                  </div>

                  {/* Funnel bar */}
                  <div>
                    <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
                      <span>Started {v.started} → Completed {v.completed}</span>
                      <span className="font-semibold">{convPct}%</span>
                    </div>
                    <div className="h-2 bg-gray-100 rounded overflow-hidden">
                      <div
                        className="h-full bg-gradient-to-r from-pink-400 to-emerald-500"
                        style={{ width: `${Math.min(convPct, 100)}%` }}
                      />
                    </div>
                  </div>

                  {/* Channel mix */}
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Channel mix (all {label.toLowerCase()} this period)</p>
                    <div className="flex h-4 rounded overflow-hidden bg-gray-100 text-[10px] text-white font-semibold">
                      {['whatsapp', 'web', 'mobile', 'other'].map(ch => {
                        const val = v.channel_mix[ch] || 0;
                        const pct = (val / mixTotal) * 100;
                        if (pct === 0) return null;
                        const color = ch === 'whatsapp' ? 'bg-pink-500' :
                                      ch === 'web' ? 'bg-indigo-500' :
                                      ch === 'mobile' ? 'bg-emerald-500' : 'bg-gray-400';
                        return (
                          <div key={ch} className={`${color} flex items-center justify-center`} style={{ width: `${pct}%` }}>
                            {pct >= 10 && `${Math.round(pct)}%`}
                          </div>
                        );
                      })}
                    </div>
                    <div className="flex gap-3 text-[11px] mt-1 text-gray-500">
                      <span>WhatsApp {v.channel_mix.whatsapp || 0}</span>
                      <span>Web {v.channel_mix.web || 0}</span>
                      <span>Mobile {v.channel_mix.mobile || 0}</span>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Discovery Boost composer — cross-sell an under-used vertical */}
        <Card className="p-5 space-y-4" data-testid="discovery-boost-card">
          <div className="flex items-start justify-between flex-wrap gap-2">
            <div>
              <h3 className="font-semibold text-sm flex items-center gap-2">
                <Zap className="h-4 w-4 text-pink-500" /> Discovery Boost
              </h3>
              <p className="text-xs text-gray-500 mt-1">
                Cross-sell an under-used vertical to users who already trust WhatsApp. Audience = booked source via WhatsApp, haven&apos;t tried target yet.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={() => setBoostOpen(!boostOpen)} data-testid="boost-toggle-btn">
              {boostOpen ? 'Close' : 'Compose'}
            </Button>
          </div>

          {boostOpen && (
            <div className="space-y-3 border-t pt-3">
              <div className="grid md:grid-cols-3 gap-3">
                <div>
                  <Label>From (source)</Label>
                  <select
                    value={boost.source_vertical}
                    onChange={(e) => { setBoost({ ...boost, source_vertical: e.target.value }); setBoostEligible(null); }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="boost-source-select"
                  >
                    <option value="packages">Packages</option>
                    <option value="rides">Rides</option>
                    <option value="bus">Bus</option>
                    <option value="cargo">Cargo</option>
                  </select>
                </div>
                <div>
                  <Label>Discover (target)</Label>
                  <select
                    value={boost.target_vertical}
                    onChange={(e) => { setBoost({ ...boost, target_vertical: e.target.value }); setBoostEligible(null); }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm"
                    data-testid="boost-target-select"
                  >
                    <option value="cargo">Cargo</option>
                    <option value="bus">Bus</option>
                    <option value="packages">Packages</option>
                    <option value="rides">Rides</option>
                  </select>
                </div>
                <div>
                  <Label>Channel</Label>
                  <div className="flex gap-2 mt-1">
                    {['whatsapp', 'push'].map(ch => (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => setBoost({ ...boost, channel: ch })}
                        className={`px-3 py-1.5 rounded-full text-xs font-medium border ${boost.channel === ch ? 'bg-pink-600 text-white border-pink-600' : 'bg-white text-gray-600 border-gray-300'}`}
                        data-testid={`boost-channel-${ch}`}
                      >{ch.toUpperCase()}</button>
                    ))}
                  </div>
                </div>
              </div>
              <div>
                <Label>Message</Label>
                <textarea
                  value={boost.message}
                  onChange={(e) => setBoost({ ...boost, message: e.target.value })}
                  maxLength={320}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm h-20"
                  data-testid="boost-message"
                />
                <p className="text-xs text-gray-500 mt-1">{boost.message.length}/320</p>
              </div>
              <div className="flex items-center justify-between flex-wrap gap-2">
                <p className="text-sm text-gray-600">
                  {boostEligible === null ? 'Run preview to see audience' : (
                    <span><strong>{boostEligible}</strong> eligible user(s)</span>
                  )}
                </p>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={previewBoost} disabled={boostBusy} data-testid="boost-preview-btn">
                    {boostBusy ? 'Working…' : 'Preview count'}
                  </Button>
                  <Button
                    onClick={sendBoost}
                    disabled={boostBusy || boostEligible === 0}
                    className="bg-pink-600 hover:bg-pink-700 text-white"
                    data-testid="boost-send-btn"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {boostBusy ? 'Sending…' : 'Send boost'}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {boostHistory.length > 0 && (
            <div className="border-t pt-3">
              <p className="text-xs font-semibold text-gray-600 mb-2">Recent boosts · revenue attribution (72h window)</p>
              <ul className="divide-y text-xs">
                {boostHistory.map(h => {
                  const booked = h.conversion?.booked || 0;
                  const gmv = h.conversion?.gmv || 0;
                  const convPct = h.sent ? Math.round((booked / h.sent) * 100) : 0;
                  return (
                    <li key={h.id} className="py-2 flex items-center justify-between gap-3" data-testid={`boost-row-${h.id}`}>
                      <div className="flex-1 min-w-0">
                        <p>
                          <span className="capitalize">{h.source_vertical}</span>
                          {' → '}
                          <span className="capitalize font-semibold">{h.target_vertical}</span>
                          {' · '}
                          <span className="text-gray-400">{(h.channel || 'whatsapp').toUpperCase()}</span>
                        </p>
                        <p className="text-[11px] text-gray-500">
                          Sent <strong>{h.sent}/{h.eligible}</strong>
                          {booked > 0 && (
                            <> · Booked <strong>{booked}</strong> within 72h ({convPct}%)</>
                          )}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className={`font-bold ${gmv > 0 ? 'text-emerald-600' : 'text-gray-400'}`}>
                          {fmtK(gmv)}
                        </p>
                        <p className="text-[10px] uppercase text-gray-400 tracking-wide">GMV unlocked</p>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </Card>

        {/* Drop-off stages */}
        <Card className="p-5" data-testid="analytics-dropoff-card">
          <h3 className="font-semibold mb-3 text-sm">Drop-off by stage (open sessions)</h3>
          {dropOff.length === 0 ? (
            <p className="text-xs text-gray-500">No active sessions in this period.</p>
          ) : (
            <ul className="space-y-1 text-sm">
              {dropOff.map(s => (
                <li key={s.stage} className="flex items-center justify-between py-1 border-b last:border-0">
                  <span className="font-mono text-xs">{s.stage}</span>
                  <span className="font-bold text-gray-700">{s.count}</span>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </AdminLayout>
  );
};

export default AdminWhatsAppAnalytics;
